class SilverBulletInstallationPreference extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.status = this.querySelector('[data-sb-preference-status]');
    this.specificWeekFields = this.querySelector('[data-sb-specific-week-fields]');
    this.travelNoteField = this.querySelector('[data-sb-travel-note-field]');
    this.preferredWeek = this.querySelector('input[name="attributes[Preferred week beginning]"]');
    this.alternateWeek = this.querySelector('input[name="attributes[Alternate week beginning]"]');
    this.travelNotes = this.querySelector('textarea[name="attributes[Travel notes]"]');
    this.pendingAttributes = null;
    this.saveTimer = null;
    this.saving = false;

    this.querySelectorAll('input[name="attributes[Preferred installation timing]"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.updateConditionalFields(true);
        this.queueSave(0);
      });
    });

    [this.preferredWeek, this.alternateWeek].forEach((input) => {
      if (!input) return;
      input.addEventListener('change', () => {
        this.enforceDateMinimum(input);
        this.queueSave(0);
      });
    });

    if (this.travelNotes) {
      this.travelNotes.addEventListener('input', () => this.queueSave(450));
      this.travelNotes.addEventListener('change', () => this.queueSave(0));
    }

    const valuesChanged = this.updateConditionalFields(true);
    if (this.dataset.preferenceNeedsSave === 'true' || valuesChanged) {
      this.queueSave(0);
    }
  }

  getSelectedTiming() {
    return (
      this.querySelector('input[name="attributes[Preferred installation timing]"]:checked')?.value ||
      'First available appointment'
    );
  }

  updateConditionalFields(clearHiddenValues = false) {
    const timing = this.getSelectedTiming();
    const showSpecificWeek = timing === 'I prefer a specific week';
    const showTravelNotes = timing !== 'First available appointment';
    let valuesChanged = false;

    if (this.specificWeekFields) this.specificWeekFields.hidden = !showSpecificWeek;
    if (this.travelNoteField) this.travelNoteField.hidden = !showTravelNotes;

    [this.preferredWeek, this.alternateWeek].forEach((input) => {
      if (!input) return;
      input.disabled = !showSpecificWeek;
      input.required = showSpecificWeek && input === this.preferredWeek;
      if (!showSpecificWeek && clearHiddenValues && input.value) {
        input.value = '';
        valuesChanged = true;
      }
      if (showSpecificWeek && this.enforceDateMinimum(input)) {
        valuesChanged = true;
      }
    });

    if (this.travelNotes) {
      this.travelNotes.disabled = !showTravelNotes;
      if (!showTravelNotes && clearHiddenValues && this.travelNotes.value) {
        this.travelNotes.value = '';
        valuesChanged = true;
      }
    }

    return valuesChanged;
  }

  enforceDateMinimum(input) {
    if (!input?.value || !input.min || input.value >= input.min) return false;

    input.value = '';
    this.setStatus(`Choose an installation week beginning on or after ${input.min}.`, true);
    return true;
  }

  getAttributes() {
    return {
      'Preferred installation timing': this.getSelectedTiming(),
      'Preferred week beginning': this.preferredWeek?.value || '',
      'Alternate week beginning': this.alternateWeek?.value || '',
      'Travel notes': this.travelNotes?.value.trim() || ''
    };
  }

  queueSave(delay) {
    this.pendingAttributes = this.getAttributes();
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.flushSaveQueue(), delay);
  }

  async flushSaveQueue() {
    if (this.saving || !this.pendingAttributes) return;

    const attributes = this.pendingAttributes;
    this.pendingAttributes = null;
    this.saving = true;
    this.setStatus('Saving installation preference...');

    try {
      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig()
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
            };

      const response = await fetch(window.routes.cart_update_url, {
        ...config,
        body: JSON.stringify({ attributes })
      });
      const state = await response.json();

      if (!response.ok || state.errors) {
        throw new Error('Shopify could not save the cart attributes.');
      }

      this.setStatus('Installation preference saved.');
    } catch (error) {
      this.setStatus('Could not save automatically. Your selection will still submit with checkout.', true);
    } finally {
      this.saving = false;
      if (this.pendingAttributes) this.flushSaveQueue();
    }
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('is-error', isError);
  }
}

if (!customElements.get('silver-bullet-installation-preference')) {
  customElements.define('silver-bullet-installation-preference', SilverBulletInstallationPreference);
}

const syncSilverBulletCartState = () => {
  const cartPage = document.getElementById('main-cart');
  if (!cartPage) return;
  cartPage.classList.toggle('sb-cart-page', Boolean(cartPage.querySelector('.sb-cart-review')));
  normalizeSilverBulletQuantities();
};

let silverBulletQuantityNormalizationInProgress = false;

const normalizeSilverBulletQuantities = async () => {
  if (silverBulletQuantityNormalizationInProgress) return;

  const fixedQuantityInputs = Array.from(document.querySelectorAll('[data-sb-fixed-quantity-input]'));
  const updates = {};

  fixedQuantityInputs.forEach((input) => {
    input.value = '1';
    if (Number(input.dataset.currentQuantity) !== 1 && input.dataset.lineKey) {
      updates[input.dataset.lineKey] = 1;
    }
  });

  if (Object.keys(updates).length === 0) return;

  silverBulletQuantityNormalizationInProgress = true;
  const status = document.querySelector('[data-sb-quantity-status]');
  if (status) {
    status.setAttribute('role', 'status');
    status.querySelector('p').textContent = 'Resetting this configured system to quantity 1...';
  }

  try {
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig()
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
          };

    const response = await fetch(window.routes.cart_update_url, {
      ...config,
      body: JSON.stringify({ updates })
    });
    const state = await response.json();

    if (!response.ok || state.errors) {
      throw new Error('Shopify could not enforce the launch quantity.');
    }

    window.location.reload();
  } catch (error) {
    silverBulletQuantityNormalizationInProgress = false;
    if (status) {
      status.setAttribute('role', 'alert');
      status.querySelector('p').textContent =
        'Quantity must be 1 before checkout. Use Update Cart or remove this line and configure it again.';
    }
  }
};

const silverBulletCartPage = document.getElementById('main-cart');
if (silverBulletCartPage) {
  syncSilverBulletCartState();
  new MutationObserver(syncSilverBulletCartState).observe(silverBulletCartPage, {
    childList: true,
    subtree: true
  });
}

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form#cart');
  if (!form) return;

  form.querySelectorAll('[data-sb-fixed-quantity-input]').forEach((input) => {
    input.value = '1';
  });

  if (event.submitter?.name !== 'checkout' || form.dataset.sbCartIntegrityBlocked !== 'true') return;

  event.preventDefault();
  const summary = document.querySelector('[data-sb-integrity-summary]');
  summary?.focus();
  summary?.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-sb-remove-reconfigure]');
  if (!trigger) return;

  event.preventDefault();
  if (trigger.classList.contains('is-loading')) return;

  const confirmed = window.confirm(
    'Remove this configured Silver Bullet system and open the exact product and variant to edit its vehicle details?'
  );
  if (!confirmed) return;

  const lineKey = trigger.dataset.lineKey;
  const productUrl = new URL(trigger.dataset.productUrl || window.routes.cart_url, window.location.origin);
  const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
  if (previewThemeId) productUrl.searchParams.set('preview_theme_id', previewThemeId);
  const status = trigger.parentElement?.querySelector('[data-sb-reconfigure-status]');

  trigger.classList.add('is-loading');
  trigger.setAttribute('aria-disabled', 'true');
  if (status) status.textContent = 'Removing this configuration and opening its vehicle details...';

  try {
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig()
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
          };

    const response = await fetch(window.routes.cart_change_url, {
      ...config,
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
    const state = await response.json();

    if (!response.ok || state.errors) {
      throw new Error('Shopify could not remove the configured line.');
    }

    window.location.assign(`${productUrl.pathname}${productUrl.search}${productUrl.hash}`);
  } catch (error) {
    trigger.classList.remove('is-loading');
    trigger.removeAttribute('aria-disabled');
    if (status) status.textContent = 'The item could not be removed. Please try again.';
  }
});